import type { SubjectField } from '../api';
import { seriesOf } from '../fields';

type FieldSeriesProps = {
  field: SubjectField;
  value: string;
  /** Present on the editable sheet: one click picks (or, on the picked one, clears) a value. */
  onPick?: (value: string) => void;
};

/** "Blok 1 ② 3 4": the whole row of a field, the chosen value ringed in the accent colour. */
export function FieldSeries({ field, value, onPick }: FieldSeriesProps) {
  const options = seriesOf(field);
  if (!options) return null;
  const mark = 'rounded-full ring-2 ring-[var(--accent)] font-bold';
  return (
    <span role="group" aria-label={field.label} className="flex flex-wrap items-center gap-x-1 text-[0.85em] leading-snug">
      <span className="mr-1 font-semibold">{field.label}</span>
      {options.map((option) => {
        const chosen = option === value;
        return onPick ? (
          <button
            key={option}
            type="button"
            aria-pressed={chosen}
            onClick={(event) => {
              event.stopPropagation(); // the cell behind it opens its editor on click; a pick is not that
              onPick(chosen ? '' : option);
            }}
            className={`min-h-6 min-w-6 px-1 ${chosen ? mark : 'opacity-80 hover:opacity-100'}`}
          >
            {option}
          </button>
        ) : (
          <span key={option} aria-current={chosen ? 'true' : undefined} className={`px-1 ${chosen ? mark : 'opacity-80'}`}>
            {option}
          </span>
        );
      })}
    </span>
  );
}
